import React, { createContext, useContext, useEffect } from 'react';
import { useAuth, useQwanyx } from 'qwanyx';
import { QwantaContext } from 'qwanyx';
import { getChildrenItems } from '../services/getChildrenItems';
import { calculateEffectiveRootId } from '../utils/stackUtils';
import { useStackLogic } from '../hooks/useStackLogic';

// Create the StackContext
const StackContext = createContext();

// Custom hook to use the StackContext
export const useStack = () => {
    const context = useContext(StackContext);
    if (!context) {
        throw new Error('useStack must be used within a StackProvider');
    }
    return context;
};

// StackProvider component
export const StackProvider = ({ children, rootId, debug = false, rows = 100 }) => {
    const { currentUser } = useAuth();
    const qwantaContext = useContext(QwantaContext);
    
    // Calculate effective root ID
    const effectiveRootId = calculateEffectiveRootId(rootId, qwantaContext, currentUser);

    const {
        cardRegistry,
        toggleCardVisibility
    } = useQwanyx();

    const isCurrentCardVisible = cardRegistry &&
        cardRegistry["currentCard"] &&
        cardRegistry["currentCard"].isVisible !== false;

    // Use the extracted logic hook
const stackLogic = useStackLogic(debug, effectiveRootId);

    const handleToggleCurrentCard = () => {
        toggleCardVisibility("currentCard");
        if (debug) {
            stackLogic.debugLog("Toggled visibility of currentCard", {
                visible: !isCurrentCardVisible
            });
        }
    };

    // Initialize root item data
    useEffect(() => {
        const fetchRootItemData = async () => {
            try {
                const children = await getChildrenItems(effectiveRootId);

                const newDataMap = {};
                children.forEach(item => {
                    newDataMap[item._id] = item;
                });

                stackLogic.setCardDataMap(prev => ({
                    ...prev,
                    ...newDataMap
                }));

                if (debug) {
                    console.log("Fetched root item children:", children);
                }
            } catch (error) {
                console.error("Error fetching root item data:", error);
                if (debug) {
                    alert(`DEBUG - Error fetching root item data: ${error.message}`);
                }
            }
        };

        fetchRootItemData();
    }, [effectiveRootId, debug, stackLogic.setCardDataMap]);

    // Context value
    const value = {
        // From useStackLogic
        ...stackLogic,
        
        // Additional state and methods
        effectiveRootId,
        isCurrentCardVisible,
        handleToggleCurrentCard,
        
        // Config
        debug,
        rows
    };

    return (
        <StackContext.Provider value={value}>
            {children}
        </StackContext.Provider>
    );
};
    